// search-rules — the typed search's WORD rules, pure, in the shared workspace
// (ADR-072 §4 · ADR-080 §4): the words of a typed text, a text as a KEY, the
// letter distance, the words a mistake is forgiven into, and the ONE in-memory
// matcher the browser runs — the pickers' lists, the client list search
// (useListController) and the admin exports' mirrors (ADR-045: the file matches
// the screen). The server's builder (backend/src/db/search.ts) imports the same
// functions, so the database and the browser forgive the same mistakes; the
// builder's own corpus is replayed through `matchRows` (db/search.test.ts).
//
// The rules, in order (the builder's header carries the same list):
//   1. A KEY — a code that names ONE row (a site's reference, a username) —
//      typed exactly and whole, with or without its separators, answers its
//      row ALONE. A key is a code too: typed in part it matches as typed like
//      any code, in its written form and in its joined form ("dmm010" finds
//      DMM-0102 — the SQL `search_ref` column holds a key both ways).
//   2. Otherwise every typed word must match SOME field as typed (AND across
//      words, OR across fields): the folded name or the folded codes, by
//      contains. Ranked per word 0 a whole word · 1 the start of a word · 2
//      inside a word, summed — never popularity or recency.
//   3. Only a word that matches NOTHING as typed in the list is forgiven, and
//      into the WORDS THE LIST'S NAMES HOLD, by a true letter distance: one
//      mistake from 4 letters, two from 8 (the whole word, or — for the last
//      word typed, from 5 letters — a word's start); a code is never forgiven.
//      A forgiven word then matches as a whole word of the name and ranks after
//      every word matched as typed, the closest first.
// The population a word is forgiven into is the list as the caller holds it —
// on the server the list as it is filtered (the viewer's reach, every lens but
// the search); in the browser the loaded set, its vocabulary built once per
// data set (`createMatcher`): the dropdown filters are chips the person clears.
import { searchWords } from './search-fold.js';
/** Word lengths that unlock a forgiven mistake (Algolia's defaults: one typo
 *  from 4 letters, two from 8). Measured on real place names: from 7 letters a
 *  second mistake confuses neighbouring towns (Batroun · Barouk · Hasroun). */
export const SEARCH_ONE_MISTAKE_MIN_CHARS = 4;
export const SEARCH_TWO_MISTAKES_MIN_CHARS = 8;
/** The LAST typed word is also forgiven as a word's START (one mistake) from
 *  this length — the word is still being typed. */
export const SEARCH_START_MIN_CHARS = 5;
/** The most distinct words a search runs over — a name and a code are a few
 *  words; past this a text is noise, and every word is an arm per field. */
export const SEARCH_WORDS_MAX = 8;
/** At most this many real words are kept for one typed word, the closest first
 *  — a list of near-identical names ("site0041", "site0042" …) stays bounded. */
export const SEARCH_FORGIVEN_MAX = 10;
/** The folded words of a typed query — what a row's folded fields hold. */
export function queryWords(q) {
    return q ? searchWords([q]) : [];
}
/** A typed text as a KEY: folded, its separators removed — "SA-KHO-0028",
 *  "SA KHO 0028" and "SAKHO0028" are one text (the twin of SQL `search_key()`). */
export function searchKey(q) {
    return queryWords(q).join('');
}
// Three reused rows — the distance runs over a list's whole vocabulary per
// forgiven keystroke and must not allocate per word (synchronous, so safe).
let rows = [new Int32Array(64), new Int32Array(64), new Int32Array(64)];
/** How many letters apart two folded words are — a letter dropped, added or
 *  wrong, or two neighbours swapped, each counts one (optimal string
 *  alignment). Anything past `max` answers `max + 1`. Counted on UTF-16 units:
 *  one unit a letter in every script the fold knows. */
export function lettersApart(a, b, max) {
    const n = a.length;
    const m = b.length;
    if (n - m > max || m - n > max)
        return max + 1;
    if (m + 1 > rows[0].length)
        rows = [new Int32Array(m + 16), new Int32Array(m + 16), new Int32Array(m + 16)];
    let prev2 = rows[0];
    let prev = rows[1];
    let cur = rows[2];
    for (let j = 0; j <= m; j++)
        prev[j] = j;
    for (let i = 1; i <= n; i++) {
        cur[0] = i;
        let best = i;
        const ai = a.charCodeAt(i - 1);
        for (let j = 1; j <= m; j++) {
            const bj = b.charCodeAt(j - 1);
            let v = prev[j - 1] + (ai === bj ? 0 : 1);
            if (prev[j] + 1 < v)
                v = prev[j] + 1;
            if (cur[j - 1] + 1 < v)
                v = cur[j - 1] + 1;
            if (i > 1 && j > 1 && ai === b.charCodeAt(j - 2) && a.charCodeAt(i - 2) === bj && prev2[j - 2] + 1 < v)
                v = prev2[j - 2] + 1;
            cur[j] = v;
            if (v < best)
                best = v;
        }
        if (best > max)
            return max + 1;
        const spare = prev2;
        prev2 = prev;
        prev = cur;
        cur = spare;
    }
    return Math.min(prev[m], max + 1);
}
/** How many mistakes a typed word of this length is forgiven. */
export function mistakesAllowed(word) {
    if (word.length >= SEARCH_TWO_MISTAKES_MIN_CHARS)
        return 2;
    return word.length >= SEARCH_ONE_MISTAKE_MIN_CHARS ? 1 : 0;
}
/** Is the word's START one letter from the typed text? The typed text against
 *  the word's first letters — one letter fewer, as many, one more (a letter
 *  dropped · wrong or swapped · added). */
function startsOneLetterAway(typed, word) {
    for (let size = typed.length - 1; size <= typed.length + 1; size++) {
        if (size < word.length && lettersApart(typed, word.slice(0, size), 1) === 1)
            return true;
    }
    return false;
}
/** The real words a typed word is forgiven into, the closest first: a word of
 *  the vocabulary one letter away (two from 8 letters), or — for the LAST word
 *  typed, from 5 letters — a word whose START is one letter away. A word that
 *  can be read two ways takes its closest reading: one letter away at its
 *  start before two letters away as a whole. */
export function forgivenWords(typed, vocabulary, isLast) {
    const allowed = mistakesAllowed(typed);
    if (allowed === 0)
        return [];
    const tryStart = isLast && typed.length >= SEARCH_START_MIN_CHARS;
    const found = [];
    for (const word of vocabulary) {
        // A word that holds the typed text matches AS TYPED — never a mistake.
        if (word.includes(typed))
            continue;
        const first = word.charCodeAt(0) === typed.charCodeAt(0) ? 0 : 1;
        const apart = lettersApart(typed, word, allowed);
        if (apart === 1)
            found.push({ word, score: first });
        else if (tryStart && startsOneLetterAway(typed, word))
            found.push({ word, score: 2 + first });
        else if (apart === 2 && allowed === 2)
            found.push({ word, score: 4 + first });
    }
    return found.sort((x, y) => x.score - y.score || (x.word < y.word ? -1 : 1)).slice(0, SEARCH_FORGIVEN_MAX);
}
/** The rank of one typed word on one row — the builder's tiers, judged over
 *  the name and the codes alike: 0 a whole word · 100 a word's start · 200
 *  inside a word; a forgiven word 300 + its closeness, as a whole word of the
 *  NAME; null = the row does not match this word. Integers on purpose: the
 *  ranks are SUMMED per word and rows equal by the rules must tie exactly (the
 *  SQL twin sums exact numerics — 0 · 1 · 2 · 3.0x — in the same order). */
function wordRank(p, word, forgiven) {
    const fields = p.codes ? [p.name, p.codes] : [p.name];
    for (const f of fields)
        if (f && ` ${f} `.includes(` ${word} `))
            return 0;
    for (const f of fields)
        if (f && ` ${f}`.includes(` ${word}`))
            return 100;
    for (const f of fields)
        if (f && f.includes(word))
            return 200;
    for (const fw of forgiven)
        if (p.name && ` ${p.name} `.includes(` ${fw.word} `))
            return 300 + fw.score;
    return null;
}
/** Prepares a list ONCE — its folded fields and, on the first forgiven word,
 *  the distinct words its names hold — and answers any number of typed texts
 *  over it. The browser keeps one matcher per data set (a memo on the rows). */
export function createMatcher(rows, fieldsOf) {
    const prepared = rows.map((row) => {
        const f = fieldsOf(row);
        return {
            row,
            name: searchWords([f.name]).join(' '),
            codes: [...new Set([...searchWords([...(f.codes ?? []), ...(f.keys ?? [])]), ...(f.keys ?? []).map(searchKey).filter((k) => k !== '')])].join(' '),
            keys: (f.keys ?? []).map(searchKey).filter((k) => k !== ''),
        };
    });
    let vocabulary = null;
    const wordsOfTheList = () => (vocabulary ??= [...new Set(prepared.flatMap((p) => (p.name ? p.name.split(' ') : [])))]);
    return {
        match(q, options) {
            const typed = queryWords(q);
            if (typed.length === 0)
                return prepared.map((p) => p.row);
            // 1. A key typed exactly and whole answers its row alone.
            const key = typed.join('');
            const named = prepared.filter((p) => p.keys.includes(key));
            if (named.length > 0)
                return named.map((p) => p.row);
            // 2 · 3. Each word once, the first SEARCH_WORDS_MAX; a word that matches
            // nothing as typed in the whole list is forgiven into the list's words.
            const words = [...new Set(typed)].slice(0, SEARCH_WORDS_MAX);
            const forgiven = words.map((word, i) => {
                if (mistakesAllowed(word) === 0)
                    return [];
                const asTyped = prepared.some((p) => p.name.includes(word) || p.codes.includes(word));
                return asTyped ? [] : forgivenWords(word, wordsOfTheList(), i === words.length - 1);
            });
            const scored = [];
            for (const p of prepared) {
                let rank = 0;
                let matches = true;
                for (let i = 0; i < words.length; i++) {
                    const r = wordRank(p, words[i], forgiven[i]);
                    if (r === null) {
                        matches = false;
                        break;
                    }
                    rank += r;
                }
                if (matches)
                    scored.push({ p, rank });
            }
            // Array.prototype.sort is stable: equals keep the list's own order.
            if (options?.order === 'ranked')
                scored.sort((a, b) => a.rank - b.rank);
            return scored.map((s) => s.p.row);
        },
    };
}
/** One typed text over one list — the one-shot face of `createMatcher` (an
 *  export mirror, a test); a screen that matches per keystroke keeps a matcher. */
export function matchRows(rows, q, fieldsOf, options) {
    return createMatcher(rows, fieldsOf).match(q, options);
}
